import { useCallback, useEffect, useRef, useState } from "react";
import { Option } from "../types/table/interfaces.type";
import { useDebouncedValue } from "./useDebouncedValue";

/**
 * Options for a picker whose list is searched on the server: the first
 * results load up front, then each pause in typing fetches new matches.
 * `fetchOptions` must be stable (wrap it in useCallback).
 */
export function useRemoteSearchOptions(
  fetchOptions: (search: string) => Promise<Option[]>,
  { enabled = true, delayMs = 300 }: { enabled?: boolean; delayMs?: number } = {}
) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, delayMs);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  // Every option seen so far, so a chosen value keeps its label after the
  // search moves on.
  const knownOptions = useRef(new Map<string, Option>());

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetchOptions(debouncedSearch.trim())
      .then((list) => {
        if (cancelled) return;
        list.forEach((option) =>
          knownOptions.current.set(String(option.value), option)
        );
        setOptions(list);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, enabled, fetchOptions]);

  /** The current results plus any chosen values they no longer include. */
  const withSelected = useCallback(
    (selected: Array<string | number | null | undefined>) => {
      const listed = new Set(options.map((option) => String(option.value)));
      const missing = selected
        .filter((value) => value !== "" && value != null)
        .map((value) => String(value))
        .filter((value) => !listed.has(value))
        .map((value) => knownOptions.current.get(value))
        .filter((option): option is Option => Boolean(option));
      return [...missing, ...options];
    },
    [options]
  );

  return { search, setSearch, options, withSelected, loading };
}
